import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import ReactDOMServer from 'react-dom/server'
import { useDispatch, useSelector } from 'react-redux'
import { withTranslation, useTranslation } from 'react-i18next'
import {
  ChevronLeftRounded as SmallIcon,
  ChevronRightRounded as LargeIcon,
  BarChartRounded as ChartIcon,
  RefreshRounded as RefreshIcon,
} from '@material-ui/icons'

import { Duration } from 'luxon'
import api from '../../../services/api'

import { getAvailability, isNumber } from '../../../utils/customMethods'
import DateTime from 'luxon/src/datetime.js'

import { useSnackbar } from 'notistack'

import {
  getMetrics,
  setMetrics,
  metricsError,
  resetMetricsStatus,
  updateMetrics,
  expandedMetricsArea,
} from '../../../redux/goals/actions'

import Tooltip from '../../atoms/display/tooltip'
import Icon from '../../atoms/display/icon'
import IconButton from '../../atoms/inputs/iconButton'
import Button from '../../atoms/inputs/button'
import CircularProgress from '../../atoms/feedback/circularProgress'
import AvatarUser from '../../molecules/avatar/user'
import Metric from '../../molecules/metrics/metric'
import UserMenu from './userMenu'
import StatusDuration from '../../molecules/metrics/statusDuration'

import useStyles from './mainStyle'
import { setActiveAvailability } from '../../../redux/availabilities/actions'
import StoreContext from '../../../store/Context'
import { useTheme } from '@material-ui/core'
import { setExpanded } from '../../../redux/expanded/actions'

const Main = (props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()

  const user = useSelector((state) => state.user)
  const {
    data: goals,
    update: updateComponent,
    lastUpdate,
    expanded: large,
  } = useSelector((state) => state.metrics)
  const { data: availabilities, activeAvailability } =
    useSelector((state) => state.availabilities) || {}

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [firstLoad, setFirstLoad] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [timer, setTimer] = useState(null)
  const intervalRef = React.useRef(null)

  const { enqueueSnackbar } = useSnackbar()
  const { setToken } = useContext(StoreContext)

  const handleExpandedArea = useCallback((state) => {
    dispatch(expandedMetricsArea(state))
  }, [])

  let useStylesParams = {
    large: large,
  }
  const classes = useStyles(useStylesParams)

  const handleClickOpenUserMenu = () => {
    setUserMenuOpen((prev) => !prev)
  }

  const loadMetrics = async () => {
    if (user?.id) {
      dispatch(getMetrics())
      try {
        const response = await api.get(`/goal-user`)

        if (response.status === 200) {
          dispatch(setMetrics(response.data?.data))
          setRefreshing(false)
        } else {
          throw response.data
        }
      } catch (error) {
        const message = error?.response?.data?.message ?? 'Unavailable service'
        dispatch(metricsError({ action: 'get_metrics', message: message }))
      }
      dispatch(resetMetricsStatus('get_metrics'))
    }
  }

  const getDuration = (seconds, format) => {
    seconds = parseInt(seconds)
    return Number.isInteger(seconds)
      ? Duration.fromObject({
          seconds: seconds,
        }).toFormat(format)
      : '-'
  }

  const translateStatus = (name) => {
    if (name === 'Online') return 0
    if (name === 'Volto logo') return 1
    if (name === 'Ausente') return 3
    return 4
  }

  const status = useMemo(() => {
    const avail = getAvailability(availabilities, activeAvailability)

    return translateStatus(avail?.typeInfo)
  }, [availabilities, activeAvailability])

  const addInfo = (metric) => {
    const updatedAt = DateTime.fromJSDate(new Date(lastUpdate))
      .setLocale(t('translations.locale'))
      .toFormat(`D - tt`)

    const header = ReactDOMServer.renderToString(
      <div className={classes.tooltipHeader}>
        {`${t('commons.analysisInterval')}:
        ${metric?.interval ? metric.interval + 'h' : '-'}`}
      </div>
    )
    const footer = ReactDOMServer.renderToString(
      <div className={classes.lastUpdate}>
        {`${t('commons.lastUpdate')}: ${updatedAt ?? '-'}`}
      </div>
    )

    const content = metric?.goal?.tooltip ?? ''

    return header + content + footer
  }

  const setExpandedClick = (isExpanded) => {
    dispatch(setExpanded(isExpanded))
  }

  const renderMetrics = useCallback(
    (isLarge) => {
      const avgTime = [
        Number(goals?.average_time?.time_attendence),
        Number(goals?.average_time?.goal?.value),
      ]
      const csat = [
        Number(goals?.csat?.csat_percentage),
        Number(goals?.csat?.goal?.value),
      ]
      const totalAttendence = [
        Number(goals?.total_attendence?.total_attendence),
        Number(goals?.total_attendence?.goal?.value),
      ]
      const nps = [
        Number(goals?.nps?.nps),
        Number(goals?.nps?.goal?.value),
      ]

      return (
        <>
          <Tooltip subtitle={addInfo(goals?.average_time)} placement="right">
            <div>
              <Metric
                title={t('commons.averageOperationTime')}
                value={getDuration(avgTime[0], 'mm:ss')}
                goal={getDuration(avgTime[1], 'mm:ss')}
                status={
                  avgTime[0] >= avgTime[1]
                    ? 3
                    : avgTime[0] >= 0.75 * avgTime[1]
                    ? 1
                    : 0
                }
                large={isLarge}
              />
            </div>
          </Tooltip>
          <div style={{ height: 10 }} />
          <Tooltip
            subtitle={addInfo(goals?.total_attendence)}
            placement="right"
          >
            <div>
              <Metric
                title={t('commons.totalAttendancesMadeOnTheDay')}
                value={isNumber(totalAttendence[0]) ? totalAttendence[0] : '-'}
                goal={isNumber(totalAttendence[1]) ? totalAttendence[1] : '-'}
                status={
                  totalAttendence[0] >= totalAttendence[1]
                    ? 0
                    : totalAttendence[0] >= 0.75 * totalAttendence[1]
                    ? 1
                    : 3
                }
                large={isLarge}
              />
            </div>
          </Tooltip>
          <div style={{ height: 10 }} />
          <Tooltip subtitle={addInfo(goals?.csat)} placement="right">
            <div>
              <Metric
                title={t('commons.averageCustomerSatisfaction')}
                value={
                  isNumber(csat[0])
                    ? csat[0].toFixed(isLarge ? 2 : 1) + '%'
                    : '-'
                }
                goal={isNumber(csat[1]) ? csat[1] + '%' : '-'}
                status={
                  csat[0] >= csat[1] ? 0 : csat[0] >= 0.75 * csat[1] ? 1 : 3
                }
                large={isLarge}
              />
            </div>
          </Tooltip>
          <div style={{ height: 10 }} />
          <Tooltip subtitle={addInfo(goals?.nps)} placement="right">
            <div>
              <Metric
                title={t('commons.netPromoterScore')}
                value={
                  isNumber(nps[0]) ? nps[0].toFixed(0) : '-'
                }
                goal={isNumber(nps[1]) ? nps[1] : '-'}
                status={
                  nps[0] >= nps[1] ? 0 : nps[0] >= 0.75 * nps[1] ? 1 : 3
                }
                large={isLarge}
              />
            </div>
          </Tooltip>
          <div style={{ height: 10 }} />
          <div>
            <StatusDuration goals={goals} large={isLarge} />
          </div>
        </>
      )
    },
    [goals, large, classes, theme]
  )

  const renderSmall = () => {
    return (
      <>
        <div className={classes.small}>
          <div>
            <div className={classes.logo}>
              <img src="/logo-small.png" alt="Porto" height={40} />
              <img
                src="/logo"
                alt="Porto"
                height={40}
                style={{ display: 'none' }}
              />
            </div>
            <div className={classes.content}>
              <Icon className={classes.iconSmall}>
                <ChartIcon style={{ color: theme.palette.primary.main }} />
              </Icon>
            </div>
          </div>
          <div className={classes.scroll}>{renderMetrics(false)}</div>
          <div className={classes.footer}>
            <div
              className={classes.linkUser}
              onClick={() => handleClickOpenUserMenu()}
            >
              <AvatarUser user={user} status={status} />
            </div>
            <div style={{ height: 20 }} />
            <Button
              size="large"
              color="primary"
              variant="contained"
              fullWidth={true}
              onClick={() => {
                handleExpandedArea(true)
                setExpandedClick(false)
              }}
            >
              <LargeIcon style={{ color: '#fff' }} />
            </Button>
          </div>
        </div>
      </>
    )
  }

  const renderLarge = () => {
    return (
      <>
        <div className={classes.large}>
          <div>
            <div className={classes.logo}>
              <img src="/logo.png" alt="Porto" height={40} />
              <img
                src="/logo-small.png"
                alt="Porto"
                height={40}
                style={{ display: 'none' }}
              />
            </div>
            <div className={classes.content}>
              <Icon
                text={t('commons.metrics')}
                color={theme.palette.primary.main}
              >
                <ChartIcon style={{ color: theme.palette.primary.main }} />
              </Icon>
              <IconButton
                color="primary"
                style={{ height: 42, width: 42 }}
                onClick={() => {
                  setRefreshing(true)
                  loadMetrics()
                }}
              >
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </div>
          </div>

          <div className={classes.scroll}>{renderMetrics(true)}</div>
          <div className={classes.footer}>
            <div
              className={classes.linkUser}
              onClick={() => handleClickOpenUserMenu()}
            >
              <AvatarUser
                variant="large"
                user={user}
                status={status}
                userMenuOpen={userMenuOpen}
              />
            </div>
            <div style={{ height: 20 }} />
            <Button
              size="large"
              color="primary"
              variant="contained"
              fullWidth={true}
              onClick={() => {
                handleExpandedArea(false)
                setExpandedClick(true)
              }}
            >
              <SmallIcon style={{ color: '#fff' }} />
            </Button>
          </div>
        </div>
      </>
    )
  }

  const updateLastPingUser = async () => {
    try {
      const response = await api.patch(`/user/lastping`)
      if (response.status !== 200) {
        throw response.data
      }
      const availability_id = response.data.data.availability_id

      if (availability_id) {
        if (availability_id !== activeAvailability) {
          dispatch(setActiveAvailability(availability_id))
        }
      } else {
        dispatch({ type: 'LOGOUT' })
        localStorage.removeItem('token')
        localStorage.removeItem('state')
        setToken(null)
        enqueueSnackbar(t('toasts.logoutSystem'), {
          variant: 'error',
          autoHideDuration: 60000,
        })

        setTimeout(() => window.location.replace('/signin'), 2000)
      }
    } catch (error) {
      throw error
    }
  }

  useEffect(() => {
    if (user?.id) setFirstLoad(true)
  }, [user])

  useEffect(() => {
    if (firstLoad) {
      loadMetrics()
      const interval = setInterval(() => loadMetrics(), 120000)
      return () => clearInterval(interval)
    }
  }, [firstLoad])

  useEffect(() => {
    if (!availabilities?.length) return

    updateLastPingUser()
    intervalRef.current = setTimeout(() => setTimer(new Date()), 120000)
    return () => intervalRef.current && clearTimeout(intervalRef.current)
  }, [availabilities, timer])

  useEffect(() => {
    if (status === 3) {
      setTimer(new Date())
    }
  }, [status])

  useEffect(() => {
    // Call api in case of update data
    if (updateComponent) {
      setTimeout(() => loadMetrics(), 1000)
      dispatch(updateMetrics(false))
    }
  }, [updateComponent])

  return (
    <div>
      {large ? renderLarge() : renderSmall()}
      {userMenuOpen && (
        <UserMenu open={setUserMenuOpen} space={large ? 200 : 80} />
      )}
    </div>
  )
}

export default withTranslation()(Main)
